/**
 * DraggableWindow - A reusable draggable/resizable window component
 */
import { useState, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { X, GripHorizontal } from 'lucide-react';
import { Rnd } from 'react-rnd';
import { cn } from '@heltarchat/ui';
import useWindowDimensions from 'utils/windowDimensions';
import { useWindowEvent } from 'utils/hooks/useWindowEvent';
import { ContainerWidthContext } from 'utils/context/ContainerWidthContext';

interface DraggableWindowProps {
  children: ReactNode;
  onClose: () => void;
  /** Unique key for saving position/size to localStorage */
  storageKey?: string;
  /** Window title */
  title?: string;
  /** Subtitle shown below title */
  subtitle?: string;
  /** Custom header content (replaces title/subtitle) */
  headerContent?: ReactNode;
  /** Show close button */
  showCloseButton?: boolean;
  /** Show drag handle bar at top */
  showDragHandle?: boolean;
  /** Overlay headerContent on a slim handle (embed-style, no separator) */
  headerOverlay?: boolean;
  /** Show resize handle at bottom-right */
  showResizeHandle?: boolean;
  /** Minimum width in pixels */
  minWidth?: number;
  /** Minimum height in pixels */
  minHeight?: number;
  /** Default width as fraction of screen width (0-1) or pixels */
  defaultWidth?: number;
  /** Default height as fraction of screen height (0-1) or pixels */
  defaultHeight?: number;
  /** Initial position: 'center' | 'center-right' | 'center-left' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' */
  initialPosition?:
    | 'center'
    | 'center-right'
    | 'center-left'
    | 'top-right'
    | 'top-left'
    | 'bottom-right'
    | 'bottom-left';
  /** Close on ESC key press */
  closeOnEsc?: boolean;
  /** Close when clicking outside the window */
  closeOnOutsideClick?: boolean;
  /** Additional class for the window container */
  className?: string;
  /** Additional class for the content area */
  contentClassName?: string;
  /** Z-index for the window */
  zIndex?: number;
}

interface WindowState {
  x: number;
  y: number;
  width: number;
  height: number;
}

const getInitialPosition = (
  position: DraggableWindowProps['initialPosition'],
  width: number,
  height: number,
  screenWidth: number,
  screenHeight: number,
): { x: number; y: number } => {
  const padding = 20;

  switch (position) {
    case 'center':
      return {
        x: Math.max((screenWidth - width) / 2, padding),
        y: Math.max((screenHeight - height) / 2, padding),
      };
    case 'center-left':
      return {
        x: padding,
        y: Math.max((screenHeight - height) / 2, padding),
      };
    case 'top-right':
      return {
        x: Math.max(screenWidth - width - padding, padding),
        y: padding,
      };
    case 'top-left':
      return {
        x: padding,
        y: padding,
      };
    case 'bottom-right':
      return {
        x: Math.max(screenWidth - width - padding, padding),
        y: Math.max(screenHeight - height - padding, padding),
      };
    case 'bottom-left':
      return {
        x: padding,
        y: Math.max(screenHeight - height - padding, padding),
      };
    case 'center-right':
    default:
      return {
        x: Math.max(screenWidth - width - 50, padding),
        y: Math.max((screenHeight - height) / 2, padding),
      };
  }
};

export const DraggableWindow: React.FC<DraggableWindowProps> = ({
  children,
  onClose,
  storageKey,
  title,
  subtitle,
  headerContent,
  showCloseButton = true,
  showDragHandle = true,
  headerOverlay = false,
  showResizeHandle = true,
  minWidth = 280,
  minHeight = 200,
  defaultWidth = 0.35,
  defaultHeight = 0.6,
  initialPosition = 'center-right',
  closeOnEsc = true,
  closeOnOutsideClick = false,
  className,
  contentClassName,
  zIndex = 50,
}) => {
  const { t } = useTranslation();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();

  // Calculate default dimensions
  const calcWidth =
    defaultWidth <= 1
      ? Math.min(screenWidth * defaultWidth, 400)
      : defaultWidth;
  const calcHeight =
    defaultHeight <= 1
      ? Math.min(screenHeight * defaultHeight, 600)
      : defaultHeight;

  const [windowState, setWindowState] = useState<WindowState>(() => {
    // Try to load from localStorage if storageKey provided
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved) as WindowState;
          // Validate saved position is still within viewport
          const isValid =
            parsed.x >= 0 &&
            parsed.y >= 0 &&
            parsed.x + parsed.width <= window.innerWidth + 100 &&
            parsed.y + parsed.height <= window.innerHeight + 100;
          if (isValid) return parsed;
        }
      } catch (error) {
        console.error('Failed to load window state:', error);
      }
    }

    // Calculate initial position
    const pos = getInitialPosition(
      initialPosition,
      calcWidth,
      calcHeight,
      window.innerWidth,
      window.innerHeight,
    );

    return {
      x: pos.x,
      y: pos.y,
      width: calcWidth,
      height: calcHeight,
    };
  });

  // Save window state
  const saveWindowState = useCallback(
    (newState: WindowState) => {
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(newState));
        } catch (error) {
          console.error('Failed to save window state:', error);
        }
      }
      setWindowState(newState);
    },
    [storageKey],
  );

  // Handle drag stop
  const handleDragStop = (_e: unknown, data: { x: number; y: number }) => {
    saveWindowState({
      ...windowState,
      x: data.x,
      y: data.y,
    });
  };

  const handleResize = (
    _e: unknown,
    _direction: unknown,
    ref: HTMLElement,
    _delta: unknown,
    position: { x: number; y: number },
  ) => {
    setWindowState({
      x: position.x,
      y: position.y,
      width: ref.offsetWidth,
      height: ref.offsetHeight,
    });
  };

  // Handle resize stop
  const handleResizeStop = (
    _e: unknown,
    _direction: unknown,
    ref: HTMLElement,
    _delta: unknown,
    position: { x: number; y: number },
  ) => {
    saveWindowState({
      x: position.x,
      y: position.y,
      width: ref.offsetWidth,
      height: ref.offsetHeight,
    });
  };

  // Handle ESC key. Listener is always attached but only acts when closeOnEsc
  // is true — the cost of one no-op keydown handler is negligible and the
  // hook stays simple this way.
  useWindowEvent(
    'keydown',
    e => {
      if (closeOnEsc && e.key === 'Escape') onClose();
    },
    [closeOnEsc, onClose],
  );

  const content = (
    <Rnd
      position={{ x: windowState.x, y: windowState.y }}
      size={{ width: windowState.width, height: windowState.height }}
      minWidth={minWidth}
      minHeight={minHeight}
      bounds='window'
      dragHandleClassName='draggable-window-handle'
      cancel='.draggable-window-buttons'
      className={cn('group/draggable-window', className)}
      style={{
        zIndex,
        position: 'fixed',
        maxWidth: '100vw',
        maxHeight: 'var(--app-height, 100vh)',
      }}
      enableUserSelectHack={false}
      onDragStop={handleDragStop}
      onResize={handleResize}
      onResizeStop={handleResizeStop}
      resizeHandleComponent={
        showResizeHandle
          ? {
              bottomRight: (
                <div className='flex items-center justify-center w-8 h-8 cursor-se-resize touch-manipulation'>
                  <div className='flex items-center justify-center w-6 h-6 rounded-br-lg bg-[var(--heltar-bg-surface)] border-t border-l border-[var(--heltar-border-input)]'>
                    <GripHorizontal className='h-4 w-4 text-[var(--heltar-border-default)] rotate-[-45deg]' />
                  </div>
                </div>
              ),
            }
          : undefined
      }
      resizeHandleStyles={
        showResizeHandle
          ? {
              bottomRight: {
                bottom: 0,
                right: 0,
                cursor: 'se-resize',
              },
            }
          : undefined
      }
      enableResizing={showResizeHandle}
    >
      <div className='relative flex h-full flex-col rounded-xl border border-[var(--heltar-border-input)] bg-[var(--heltar-bg-primary)] shadow-2xl overflow-hidden'>
        {headerOverlay ? (
          showDragHandle && (
            <div className='draggable-window-handle relative flex min-h-[28px] items-center bg-[var(--heltar-bg-surface)] px-1 py-0.5 cursor-move touch-none'>
              <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-1 rounded-full bg-[var(--heltar-border-default)]' />
              {headerContent && (
                <div
                  className='draggable-window-buttons ml-auto flex items-center'
                  onPointerDown={e => e.stopPropagation()}
                  onMouseDown={e => e.stopPropagation()}
                  onTouchStart={e => e.stopPropagation()}
                >
                  {headerContent}
                </div>
              )}
            </div>
          )
        ) : (
          <>
            {showDragHandle && (
              <div className='draggable-window-handle flex flex-col items-center bg-[var(--heltar-bg-surface)] pt-2 pb-1 cursor-move touch-none'>
                <div className='w-10 h-1 rounded-full bg-[var(--heltar-border-default)] mb-1' />
              </div>
            )}

            {(title || subtitle || headerContent || showCloseButton) && (
              <div className='flex items-center justify-between bg-[var(--heltar-bg-surface)] px-3 pb-2'>
                {headerContent ? (
                  <div className='flex-1 min-w-0 mr-2'>{headerContent}</div>
                ) : (
                  <div className='flex flex-col min-w-0 flex-1 mr-2'>
                    {title && (
                      <span className='truncate text-sm font-semibold font-sans'>
                        {title}
                      </span>
                    )}
                    {subtitle && (
                      <span className='truncate text-xs text-[var(--heltar-text-secondary)]'>
                        {subtitle}
                      </span>
                    )}
                  </div>
                )}
                {showCloseButton && (
                  <div
                    className='draggable-window-buttons flex items-center gap-1'
                    onPointerDown={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                    onTouchStart={e => e.stopPropagation()}
                  >
                    <button
                      type='button'
                      onClick={onClose}
                      className={cn(
                        'rounded-full p-2.5 text-[var(--heltar-text-secondary)] transition-all',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--heltar-brand-primary)]',
                        'hover:text-[var(--heltar-text-primary)]',
                        'touch-manipulation',
                      )}
                      aria-label={t('Close')}
                    >
                      <X className='h-5 w-5' />
                    </button>
                  </div>
                )}
              </div>
            )}

            {(title || subtitle || headerContent || showCloseButton) && (
              <div className='h-px bg-[var(--heltar-border-input)]' />
            )}
          </>
        )}

        {/* Content */}
        <div
          className={cn('flex-1 overflow-auto scrollShadcn', contentClassName)}
        >
          <ContainerWidthContext.Provider value={windowState.width}>
            {children}
          </ContainerWidthContext.Provider>
        </div>
      </div>
    </Rnd>
  );

  return createPortal(
    <>
      {closeOnOutsideClick && (
        <div
          className='fixed inset-0 bg-black/30'
          style={{ zIndex: zIndex - 1 }}
          onClick={onClose}
          aria-hidden='true'
        />
      )}
      {content}
    </>,
    document.body,
  );
};
